import React, {ReactNode} from "react";
import {
	Avatar,
	Box,
	Button,
	Card,
	Chip,
	Grid,
	InputAdornment,
	Skeleton,
	Stack,
	SxProps,
	TextField,
	Typography,
} from "@mui/material";
import {alpha, Theme, useTheme} from "@mui/material/styles";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {useNavigate} from "react-router-dom";
import dayjs from "dayjs";
import AdminInner from "../layout/admin_inner";

/**
 * Kit común de pantallas del admin (#13877). Lo que ya usaban fichaje, entrevistas y autoevaluaciones,
 * en un solo sitio, para que todas las pantallas se vean igual:
 *
 * - Página: {@link Page} y {@link PageHeader} (título, subtítulo, «Volver» y acciones).
 * - Bloques: {@link Panel} (tarjeta con título) y {@link StatTile} (una cifra con su icono).
 * - Estados: {@link SoftChip}, {@link StatusDot}, {@link EmptyState}, {@link SkeletonRows}.
 * - Personas: {@link PersonAvatar}, {@link fullName}, {@link initials}.
 * - Listados: {@link FilterChips}, {@link SearchField}, {@link tableSx}, {@link ListCard}.
 *
 * Textos en español; nombres en inglés. Sin mayúsculas forzadas en botones ni cabeceras.
 *
 * Cuándo usar cada pieza, patrones de listado, ficha y panel, trampas y cómo validar: el sistema de
 * diseño, `docs/ref-diseno-admin.md` del cuaderno de la Extranet (enlazado desde AGENTS.md).
 */

// ── Fundamentos ──────────────────────────────────────────────────────────────────────────────

export type Tone = "success" | "warning" | "error" | "info" | "neutral";

export const toneColor = (theme: Theme, tone: Tone): string => tone === "neutral" ? theme.palette.grey[500] : theme.palette[tone].main;

/** Tarjeta: borde fino, sombra mínima y esquinas redondeadas. */
export const panelSx = {
	borderRadius: 3,
	border: "1px solid",
	borderColor: "divider",
	boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
	p: {xs: 2.5, sm: 3},
};

/** Botón sin mayúsculas forzadas. */
export const btnSx = {textTransform: "none", fontWeight: 600, borderRadius: 2} as const;

export const AVATAR_COLORS = ["#2445B8", "#0f8f76", "#8e44ad", "#c0392b", "#d35400", "#16a085", "#2c3e50", "#7f8c8d"];

export const fullName = (p?: { name?: string, surnames?: string }) => p ? `${p.name || ""} ${p.surnames || ""}`.trim() : "";

export const initials = (name?: string) => (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase();

export const avatarColor = (name?: string) => {
	let h = 0;
	for (const c of name || "") {
		h = (h * 31 + c.charCodeAt(0)) >>> 0;
	}
	return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

/** «hoy», «en 5 días», «hace 3 semanas», «hace 2 años». */
export const relative = (date?: string): string => {
	if (!date) {
		return "";
	}
	const days = dayjs(date).startOf("day").diff(dayjs().startOf("day"), "day");
	const abs = Math.abs(days);
	const unit = abs < 14 ? [abs, abs === 1 ? "día" : "días"]
		: abs < 60 ? [Math.round(abs / 7), "semanas"]
			: abs < 730 ? [Math.round(abs / 30), "meses"]
				: [Math.floor(abs / 365), "años"];
	if (days === 0) {
		return "hoy";
	}
	return days > 0 ? `en ${unit[0]} ${unit[1]}` : `hace ${unit[0]} ${unit[1]}`;
};

/** «1.234,50 €» */
export const euros = (n?: number | null, decimals = 2) => n === undefined || n === null ? "—"
	// useGrouping "always": en es-ES el separador de miles no sale con cuatro cifras («3024,00 €»).
	: n.toLocaleString("es-ES", {minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: "always"} as unknown as Intl.NumberFormatOptions) + " €";

// ── Página ───────────────────────────────────────────────────────────────────────────────────

/** Contenedor de una pantalla: sin el papel blanco del layout, centrado y con ancho máximo. */
export const Page = ({children, maxWidth = 1180}: { children: ReactNode, maxWidth?: number }) => (
	<AdminInner withoutPaper>
		<Box sx={{maxWidth, mr: "auto"}}>{children}</Box>
	</AdminInner>
);

interface PageHeaderProps {
	title: ReactNode;
	subtitle?: ReactNode;
	/** true: vuelve a la anterior; una ruta: va a esa. */
	back?: boolean | string;
	actions?: ReactNode;
}

export const PageHeader = ({title, subtitle, back, actions}: PageHeaderProps) => {
	const navigate = useNavigate();
	return (
		<Stack direction={{xs: "column", sm: "row"}} spacing={2} justifyContent="space-between" alignItems={{sm: "flex-end"}} sx={{mb: 3}}>
			<Box sx={{minWidth: 0}}>
				{back && (
					<Button size="small" startIcon={<ArrowBackRoundedIcon/>}
							onClick={() => typeof back === "string" ? navigate(back) : navigate(-1)}
							sx={{...btnSx, ml: -1, mb: 0.5, color: "text.secondary"}}>
						Volver
					</Button>
				)}
				<Typography variant="h5" component="h1" fontWeight={700}>{title}</Typography>
				{subtitle && <Typography variant="body2" color="text.secondary" component="div">{subtitle}</Typography>}
			</Box>
			{actions && <Stack direction="row" spacing={1} sx={{flexShrink: 0, alignSelf: {xs: "flex-start", sm: "auto"}}}>{actions}</Stack>}
		</Stack>
	);
};

// ── Bloques ──────────────────────────────────────────────────────────────────────────────────

interface PanelProps {
	title?: ReactNode;
	subtitle?: ReactNode;
	icon?: ReactNode;
	/** A la derecha del título: un botón, un total… */
	action?: ReactNode;
	children?: ReactNode;
	sx?: SxProps<Theme>;
}

export const Panel = ({title, subtitle, icon, action, children, sx}: PanelProps) => (
	<Card elevation={0} sx={{...panelSx, ...(sx as object)}}>
		{(title || action) && (
			<Stack direction={{xs: "column", sm: "row"}} spacing={{xs: 1.5, sm: 2}} justifyContent="space-between"
				   alignItems={{xs: "stretch", sm: "flex-start"}} sx={{mb: children ? 2 : 0}}>
				<Box sx={{minWidth: 0}}>
					<Stack direction="row" spacing={1} alignItems="center">
						{icon}
						<Typography variant="subtitle1" component="h2" fontWeight={700}>{title}</Typography>
					</Stack>
					{subtitle && <Typography variant="body2" color="text.secondary" sx={{mt: 0.25}}>{subtitle}</Typography>}
				</Box>
				{action && <Box sx={{flexShrink: 0}}>{action}</Box>}
			</Stack>
		)}
		{children}
	</Card>
);

interface StatTileProps {
	label: ReactNode;
	value?: ReactNode;
	icon?: ReactNode;
	tone?: Tone;
	hint?: ReactNode;
	footer?: ReactNode;
	/** Mientras carga, un esqueleto en lugar del valor. */
	loading?: boolean;
	onClick?: () => void;
}

/** Una cifra con su icono tintado. Va dentro de un Grid container; el ancho lo decide quien la usa. */
export const StatTile = ({label, value, icon, tone = "neutral", hint, footer, loading, onClick}: StatTileProps) => {
	const theme = useTheme();
	const c = toneColor(theme, tone);
	return (
		<Box onClick={onClick} sx={{
			p: 2, borderRadius: 2.5, border: "1px solid", borderColor: "divider", height: "100%", bgcolor: "background.paper",
			cursor: onClick ? "pointer" : undefined, transition: "border-color .15s",
			"&:hover": onClick ? {borderColor: "primary.main"} : undefined,
		}}>
			<Stack direction="row" spacing={1.5} alignItems="center">
				{icon && (
					<Box sx={{
						width: 36, height: 36, borderRadius: 2, display: "grid", placeItems: "center", flexShrink: 0,
						bgcolor: alpha(c, 0.12), color: c,
					}}>{icon}</Box>
				)}
				{loading
					? <Skeleton width={48} height={36}/>
					: <Typography variant="h5" component="div" fontWeight={700} sx={{fontVariantNumeric: "tabular-nums"}}>{value}</Typography>}
			</Stack>
			<Typography variant="body2" color="text.secondary" sx={{mt: 1}}>{label}</Typography>
			{hint}
			{footer}
		</Box>
	);
};

/** Fila de cifras: en móvil, de dos en dos. */
export const StatGrid = ({children}: { children: ReactNode }) => (
	<Grid container spacing={1.5} sx={{mb: 3}}>
		{React.Children.toArray(children).filter(Boolean).map((c, i) => (
			<Grid key={i} item xs={6} sm={4} md>{c}</Grid>
		))}
	</Grid>
);

// ── Estados ──────────────────────────────────────────────────────────────────────────────────

/** Etiqueta suave: fondo tintado del color del tono, sin bloques de color macizo. */
export const SoftChip = ({label, tone, icon}: { label: ReactNode, tone: Tone, icon?: React.ReactElement }) => {
	const theme = useTheme();
	const c = toneColor(theme, tone);
	return <Chip size="small" label={label} icon={icon} sx={{
		fontWeight: 600, bgcolor: alpha(c, 0.12),
		color: tone === "neutral" ? "text.secondary" : theme.palette[tone as Exclude<Tone, "neutral">].dark,
		border: "1px solid", borderColor: alpha(c, 0.25), "& .MuiChip-icon": {color: "inherit"},
	}}/>;
};

/** Punto de color y texto. */
export const StatusDot = ({tone, label}: { tone: Tone, label: ReactNode }) => {
	const theme = useTheme();
	return (
		<Stack direction="row" spacing={0.75} alignItems="center">
			<Box sx={{width: 8, height: 8, borderRadius: "50%", bgcolor: toneColor(theme, tone), flexShrink: 0}}/>
			<Typography variant="body2" component="span">{label}</Typography>
		</Stack>
	);
};

export const EmptyState = ({icon, title, description, action}: { icon?: ReactNode, title: ReactNode, description?: ReactNode, action?: ReactNode }) => (
	<Stack alignItems="center" spacing={1} sx={{py: 5, px: 2, textAlign: "center", color: "text.secondary"}}>
		{icon && <Box sx={{color: "text.disabled", "& svg": {fontSize: 40}}}>{icon}</Box>}
		<Typography variant="body2" fontWeight={600} color="text.primary">{title}</Typography>
		{description && <Typography variant="body2" color="text.secondary" sx={{maxWidth: 420}}>{description}</Typography>}
		{action && <Box sx={{pt: 1}}>{action}</Box>}
	</Stack>
);

export const SkeletonRows = ({rows = 4, height = 52}: { rows?: number, height?: number }) => (
	<Stack spacing={1}>{Array.from({length: rows}).map((_, i) => <Skeleton key={i} variant="rounded" height={height}/>)}</Stack>
);

// ── Personas ─────────────────────────────────────────────────────────────────────────────────

export const PersonAvatar = ({name, size = 32, src}: { name?: string, size?: number, src?: string }) => (
	<Avatar src={src} sx={{width: size, height: size, fontSize: Math.round(size * 0.38), fontWeight: 700, bgcolor: avatarColor(name)}}>
		{initials(name)}
	</Avatar>
);

// ── Listados ─────────────────────────────────────────────────────────────────────────────────

export interface FilterOption<T> {
	value: T;
	label: ReactNode;
	count?: number;
}

/** Filtro de una sola opción en etiquetas; en móvil se desplaza en horizontal. */
export const FilterChips = <T, >({options, value, onChange}: { options: FilterOption<T>[], value: T, onChange: (v: T) => void }) => (
	<Stack direction="row" spacing={1} sx={{overflowX: "auto", pb: {xs: 0.5, md: 0}}}>
		{options.map((o, i) => {
			const selected = o.value === value;
			return (
				<Chip key={i} clickable onClick={() => onChange(o.value)}
					  label={o.count !== undefined ? <>{o.label} {o.count}</> : o.label}
					  color={selected ? "primary" : "default"} variant={selected ? "filled" : "outlined"}
					  sx={{fontWeight: 600, flexShrink: 0}}/>
			);
		})}
	</Stack>
);

export const SearchField = ({value, onChange, placeholder = "Buscar", width = 260}: {
	value: string, onChange: (v: string) => void, placeholder?: string, width?: number,
}) => (
	<TextField size="small" placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)}
			   sx={{width: {xs: "100%", md: width}, "& .MuiOutlinedInput-root": {borderRadius: 2}}}
			   InputProps={{startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small"/></InputAdornment>}}/>
);

/** Barra de filtros encima de un listado: filtros a la izquierda, búsqueda y conmutadores a la derecha. */
export const Toolbar = ({left, right}: { left?: ReactNode, right?: ReactNode }) => (
	<Stack direction={{xs: "column", md: "row"}} spacing={1.5} justifyContent="space-between" alignItems={{md: "center"}} sx={{mb: 2}}>
		<Box sx={{minWidth: 0}}>{left}</Box>
		{right && <Stack direction="row" spacing={2} alignItems="center">{right}</Stack>}
	</Stack>
);

/** Estilo de las tablas de listado: cabecera gris clara sin mayúsculas, filas aireadas. Para TableContainer + Table size="small". */
export const tableContainerSx = {border: "1px solid", borderColor: "divider", borderRadius: 2.5};
export const tableSx = {
	"& th": {bgcolor: "grey.50", color: "text.secondary", fontSize: 12, fontWeight: 700, py: 1.25, whiteSpace: "nowrap", textTransform: "none"},
	"& td": {py: 1.25, borderColor: "divider"},
	"& tbody tr:last-of-type td": {borderBottom: 0},
};

/** Tarjeta de un elemento en los listados de móvil; si tiene onClick, se puede pulsar y abrir con Intro. */
export const ListCard = ({children, onClick, muted, sx}: { children: ReactNode, onClick?: () => void, muted?: boolean, sx?: SxProps<Theme> }) => (
	<Box role={onClick ? "link" : undefined} tabIndex={onClick ? 0 : undefined} onClick={onClick}
		 onKeyDown={e => onClick && e.key === "Enter" && onClick()}
		 sx={{
			 p: 2, borderRadius: 2.5, border: "1px solid", borderColor: "divider", bgcolor: "background.paper",
			 cursor: onClick ? "pointer" : undefined, opacity: muted ? 0.6 : 1,
			 "&:hover": onClick ? {borderColor: "primary.main"} : undefined, ...(sx as object),
		 }}>
		{children}
	</Box>
);

/** Texto recortado a N líneas. */
export const clampSx = (lines: number) => ({display: "-webkit-box", WebkitLineClamp: lines, WebkitBoxOrient: "vertical" as const, overflow: "hidden"});
